import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Switch, Route, Redirect } from 'react-router'
import { BrowserRouter } from 'react-router-dom'

import {
  getLocalItem,
  setLocalItem,
  removeLocalItem,
} from './local-client/local-client'
import {
  checkAuthenticationAsync,
  pageAccessAttempt,
  getUserAccountDetailsGQL,
  handleGoogleResponseAsync,
  handleAdobeResponseAsync,
  loadCredentialsAsync,
  loginGoogleSSOAsync,
  loginMicrosoftSSOAsync,
  registerAsync,
  createNewClientAsync,
  logActionAsync,
  getOnboardingStatus,
} from './store/actions'
import { defaultHomepageSlugs } from './core/variables'

// view components
import { AccountState } from './store/account-reducer'
import Login from './pages/login'
import Share from './pages/share'
import DirectDownload from './pages/direct-download'
import MarketingOptOut from './pages/marketing-optout'
import SelectModule from './pages/selectmodule'
import ForgotPassword from './pages/forgot-password'
import ResetPassword from './pages/reset-password'
import Audit from './pages/audit'
import Explain from './pages/explain'
import AnomalyBreakdown from './pages/anomaly-breakdown'
import NotFound from './pages/not-found'
import Settings from './pages/settings'
import PerformanceReportPage from './pages/performance-report-page'
import DashboardPage from './pages/dashboard-page'
import Register from './pages/register'
import CalendarPage from './pages/calendar'
import CreateCampaignCodes from './pages/create-campaign-codes'
import ViewCampaignCodes from './pages/view-campaign-codes'
import EditCampaignCodes from './pages/edit-campaign-codes'
import CampaignCodesGeneratorHelp from './pages/campaign-codes-generator-help'
import SelectPlan from './pages/select-plan'
import ConnectToGoogleAnalytics from './pages/connect-to-google-analytics'
import ConnectWebsite from './pages/connect-website'
import GetStartedPage from './pages/get-started-page'
import WelcomePage from './pages/welcome-page'
// import { handleGoogleResponse } from './api-client/settings-client'
import Loading from './components/loading'
import { UserState } from './store/user-reducer'
import { LoginState } from './store/login-reducer'
import { OauthState } from './store/oauth-reducer'
import { GraphQLState } from './store/graphql-reducer'

// only when the user is logged in
function PrivateRoute({ ...props }: any): React.ReactElement {
  const dispatch = useDispatch()
  const {
    user: { authenticated },
  }: { user: UserState } = useSelector((s: any): any => s)
  // eslint-disable-next-line
  const pathName = props?.location?.pathname || ''
  // eslint-disable-next-line
  const search = props?.location?.search || ''
  const hasToken = search.indexOf('token=') !== -1
  useEffect((): void => {
    if (hasToken) return
    if (!authenticated) {
      dispatch(pageAccessAttempt(pathName + search))
    }
    // eslint-disable-next-line
  }, [authenticated, pathName])

  useEffect(() => {
    if (authenticated || hasToken) {
      const section = pathName.match(/(^\/.+\/)|(\/.+$)/gi)
      if (section && section.length > 0) {
        const actionData = {
          action: `page-load${pathName}`,
          longDescription: '',
          websiteSection: section[0].replace(/\//gi, ''),
        }
        dispatch(logActionAsync(actionData))
      }
    }
  }, [authenticated, hasToken, pathName])

  if (authenticated || hasToken) {
    return <Route {...props} />
  }

  return <Redirect to={{ pathname: '/login' }} />
}

// only when the user is logged out
function NonPrivateRoute({ ...props }: any): React.ReactElement {
  const dispatch = useDispatch()
  const {
    user: { authenticated, pageRedirect },
    graphql,
  }: { user: UserState; graphql: GraphQLState } = useSelector(
    (s: any): any => s,
  )

  // eslint-disable-next-line
  let pathName = props?.location?.pathname || ''

  useEffect((): void => {
    if (authenticated) {
      dispatch(pageAccessAttempt(pathName))
    }
  }, [authenticated])

  if (!authenticated) {
    return <Route {...props} />
  }

  const hasQuery = pageRedirect && pageRedirect.indexOf('?') !== -1
  if (hasQuery) {
    const [pathname, search] = pageRedirect.split('?')
    return <Redirect to={{ pathname, search: `?${search}` }} />
  }

  let usePath = ''
  const { currentUser, currentAccount } = graphql

  if (currentUser && currentAccount && currentUser.preferredHomepage) {
    usePath =
      currentAccount[`${currentUser.preferredHomepage}Available`] ||
      currentUser.preferredHomepage === 'welcome'
        ? defaultHomepageSlugs[currentUser.preferredHomepage]
        : '/welcome'
  }

  return <Redirect to={{ pathname: pageRedirect || usePath || '/welcome' }} />
}

interface OAuthRedirectProps {
  location: Location
  dataSource: DataSource
}

function OAuthRedirect({ location, dataSource }: OAuthRedirectProps) {
  const dispatch = useDispatch()
  const { oauth }: { oauth: OauthState } = useSelector(
    (state: any): any => state,
  )

  const { credentials, initilised, loading, state } = oauth

  useEffect(() => {
    if (!initilised) {
      if (dataSource === 'AA_REPORT') {
        dispatch(handleAdobeResponseAsync(location.search))
      } else {
        dispatch(handleGoogleResponseAsync(location.search))
      }
    }
    // eslint-disable-next-line
  }, [initilised])

  if (initilised && !loading && credentials !== '') {
    const { returnTo } = state
    const pathname = returnTo || '/connect-website'
    return <Redirect to={{ pathname }} />
  }

  return <></>
}

interface OAuthRedirectSSOProps {
  location: Location
  source: 'google' | 'microsoft'
}

function OAuthRedirectSSO({ location, source }: OAuthRedirectSSOProps) {
  const dispatch = useDispatch()
  const {
    user: { authenticated },
    login,
    graphql,
  }: {
    user: UserState
    login: LoginState
    graphql: GraphQLState
  } = useSelector((s: any): any => s)

  const {
    onFail: { attempts, message },
  } = login

  const erroredButHasAccount =
    source === 'microsoft' && message.indexOf('already got an account') !== -1

  useEffect(() => {
    if (!authenticated || erroredButHasAccount) {
      if (source === 'microsoft') {
        const { hash } = window.location
        if (hash && hash.indexOf('#') !== -1) {
          const msNonce = getLocalItem('ms-nonce')
          const msAction = getLocalItem('ms-action')
          setLocalItem('ms-action', 'progress')
          const hashQuery = new URLSearchParams(hash.split('#').pop())
          const idToken = hashQuery?.get('id_token')
          // eslint-disable-next-line
          // debugger
          // const msQState = hashQuery?.get('state')
          const msState = getLocalItem('ms-state')
          removeLocalItem('ms-state')
          if (erroredButHasAccount || !msAction || msAction === 'login') {
            dispatch(
              loginMicrosoftSSOAsync({ id_token: idToken, nonce: msNonce }),
            )
          } else if (msState) {
            const isParsed = typeof msState === 'object' && msState !== null
            if (isParsed && msAction === 'register') {
              const { fName, lName, token, acceptedMarketing } = msState
              const state = {
                fName,
                lName,
                token,
                signUpMethod: 'microsoft',
                nonce: msNonce,
                id_token: idToken,
                acceptedMarketing,
              }
              dispatch(registerAsync(state))
            } else if (isParsed && msAction === 'create') {
              const {
                fName,
                lName,
                email,
                phoneNum,
                organisation,
                acceptedMarketing,
              } = msState
              const state = {
                fName,
                lName,
                signUpMethod: 'microsoft',
                nonce: msNonce,
                id_token: idToken,
                email,
                phoneNum,
                organisation,
                acceptedMarketing,
              }
              dispatch(createNewClientAsync(state))
            }
          }
        }
      } else {
        const searchQuery = new URLSearchParams(window.location.search)
        const gaCode = searchQuery?.get('code')
        // const qState = searchQuery?.get('state')
        const gaNonce = getLocalItem('ga-nonce')
        const gaState = getLocalItem('ga-state')
        removeLocalItem('ga-state')
        const gaAction = getLocalItem('ga-action')
        setLocalItem('ga-action', 'progress')
        // eslint-disable-next-line
        // debugger
        if (!gaAction || gaAction === 'login') {
          dispatch(loginGoogleSSOAsync({ code: gaCode, nonce: gaNonce }))
        } else if (gaState) {
          const isParsed = typeof gaState === 'object' && gaState !== null
          if (isParsed && gaAction === 'register') {
            const { fName, lName, token, acceptedMarketing } = gaState
            const state = {
              fName,
              lName,
              token,
              signUpMethod: 'google',
              nonce: gaNonce,
              code: gaCode,
              acceptedMarketing,
            }
            dispatch(registerAsync(state))
          } else if (isParsed && gaAction === 'create') {
            const {
              fName,
              lName,
              email,
              phoneNum,
              organisation,
              acceptedMarketing,
            } = gaState
            const state = {
              fName,
              lName,
              signUpMethod: 'google',
              nonce: gaNonce,
              code: gaCode,
              email,
              phoneNum,
              organisation,
              acceptedMarketing,
            }
            dispatch(createNewClientAsync(state))
          }
        }
        // dispatch(handleGoogleResponseAsync(location.search))
      }
    }
    // eslint-disable-next-line
  }, [authenticated, erroredButHasAccount])

  if (!erroredButHasAccount && attempts && attempts > 0) {
    return <Redirect to={{ pathname: '/login' }} />
  }

  if (authenticated) {
    // eslint-disable-next-line
    // debugger
    // const { returnTo } = state

    let usePath = ''

    if (
      graphql &&
      graphql.currentUser &&
      graphql.currentAccount &&
      graphql.currentUser.preferredHomepage
    ) {
      usePath =
        graphql.currentAccount[
          `${graphql.currentUser.preferredHomepage}Available`
        ] || graphql.currentUser.preferredHomepage === 'welcome'
          ? defaultHomepageSlugs[graphql.currentUser.preferredHomepage]
          : '/welcome'
    }

    return <Redirect to={{ pathname: usePath || '/welcome' }} />
  }

  return <></>
}

export default function Router(): React.ReactElement | null {
  const dispatch = useDispatch()
  const {
    user: { authenticated, checked },
    graphql: { currentAccount },
    account: { accountOnboardingSections },
  }: {
    user: UserState
    graphql: GraphQLState
    account: AccountState
  } = useSelector((s: any): any => s)

  useEffect(() => {
    if (!checked) {
      dispatch(loadCredentialsAsync())
      dispatch(checkAuthenticationAsync())
    }

    if (checked && authenticated) {
      if (!currentAccount) {
        dispatch(getUserAccountDetailsGQL())
      }
      if (!accountOnboardingSections) {
        dispatch(getOnboardingStatus())
      }
    }

    const interval = setInterval((): void => {
      dispatch(checkAuthenticationAsync())
    }, 250000)

    return function cleanup(): void {
      clearInterval(interval)
    }
  }, [checked, authenticated])

  if (!checked) return <Loading />

  return (
    <BrowserRouter>
      <Switch>
        <Route
          exact
          path="/"
          component={() => <Redirect to={{ pathname: '/login' }} />}
        />
        {/* Pages you must be logged out to see */}
        <Route exact path="/share/:shareLinkID" component={Share} />
        <Route
          exact
          path="/code/direct-download/:downloadId"
          component={DirectDownload}
        />
        <Route
          exact
          path="/user-marketing-optout/:userId"
          component={MarketingOptOut}
        />
        <NonPrivateRoute exact path="/login" component={Login} />
        <NonPrivateRoute
          exact
          path="/forgot-password"
          component={ForgotPassword}
        />
        <NonPrivateRoute
          exact
          path="/reset-password"
          component={ResetPassword}
        />
        <NonPrivateRoute exact path="/register" component={Register} />
        {/* On boarding */}
        <Route exact path="/create-account" component={GetStartedPage} />
        <Route exact path="/plans" component={SelectPlan} />
        <PrivateRoute
          exact
          path="/connect"
          component={ConnectToGoogleAnalytics}
        />
        {/* after google oauth this becomes private route, user is techically logged in  */}
        <PrivateRoute
          exact
          path="/connect-website"
          component={ConnectWebsite}
        />
        {/* Private pages that need logging in to see */}
        <PrivateRoute exact path="/select-module" component={SelectModule} />
        <PrivateRoute exact path="/welcome" component={WelcomePage} />
        <PrivateRoute exact path="/audit" component={Audit} />
        <PrivateRoute exact path="/explain" component={Explain} />
        <PrivateRoute
          exact
          path="/explain/anomaly/:metricID/:anomDate"
          component={AnomalyBreakdown}
        />
        <PrivateRoute exact path="/settings" component={Settings} />
        <PrivateRoute exact path="/report/calendar" component={CalendarPage} />
        <PrivateRoute
          exact
          path="/report/performance"
          component={PerformanceReportPage}
        />
        <PrivateRoute
          exact
          path="/report/dashboards"
          component={DashboardPage}
        />
        <PrivateRoute
          exact
          path="/track/create-campaign-codes"
          component={CreateCampaignCodes}
        />
        <PrivateRoute
          exact
          path="/track/view-campaign-codes"
          component={ViewCampaignCodes}
        />
        <PrivateRoute
          exact
          path="/track/edit-campaign-codes"
          component={EditCampaignCodes}
        />
        <PrivateRoute
          exact
          path="/track/generator-help"
          component={CampaignCodesGeneratorHelp}
        />

        <Route
          exact
          path="/auth/o365-redirect"
          component={({ location }: any): React.ReactElement => (
            <OAuthRedirectSSO location={location} source="microsoft" />
          )}
        />
        <Route
          exact
          path="/auth/gsuite-redirect"
          component={({ location }: any): React.ReactElement => (
            <OAuthRedirectSSO location={location} source="google" />
          )}
        />
        <Route
          exact
          path="/oauth2callback"
          component={({ location }: any): React.ReactElement => (
            <OAuthRedirect location={location} dataSource="GA_VIEW" />
          )}
        />
        <Route
          exact
          path="/aa-oauth2callback"
          component={({ location }: any): React.ReactElement => (
            <OAuthRedirect location={location} dataSource="AA_REPORT" />
          )}
        />
        {/* 404 */}
        <Route component={NotFound} />
      </Switch>
    </BrowserRouter>
  )
}
